import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  Container,
  Divider,
  Grid,
  Link as LinkMui,
  MenuItem,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, getRepCodeName, OpsOwner } from '../helper'
import './RmaList.css'
import { useState, useEffect, useContext } from 'react'
import { rmaType } from '../types'
import dayjs from 'dayjs'
import axios from 'axios'
import { rmaStatuses } from '../lists'
import { dataContext } from '..'
import ErrorScreen from '../Misc/ErrorScreen'
import { Link as LinkRouter } from 'react-router-dom'

function RmaList() {
  const { user, salesReps, operations } = useContext(dataContext)

  const [rmaList, setRmaList] = useState<
    (rmaType & {
      so_number: string
      customer_po: string
      ops_owner: string
      rma_status: string
    })[]
  >([])
  const [rmaCount, setRmaCount] = useState(0)
  const [rmaFilter, setRmaFilter] = useState<{ [index: string]: string | string[] | number | Date | undefined }>({
    soNumber: '',
    customerPO: '',
    rmaStatus: '',
    salesRep: '',
    vendorPO: '',
    rmaInitFrom: undefined as Date | undefined,
    rmaInitTo: undefined as Date | undefined,
    caseNumber: '',
    opsOwner: '',
    limit: 25,
    page: 0,
    sort: 'rma_initiation_date desc',
  })
  const [listLoading, setListLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)
  const [searchError, setSearchError] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [opsOwnerError, setOpsOwnerError] = useState({
    show: false,
    rmaRefNumber: '',
  })

  useEffect(() => {
    if (user.active_flag) {
      handleSearch(rmaFilter)
    }
  }, [user])

  useEffect(() => {
    document.title = 'RMAs | Salestrack'
  }, [])

  const handleSearch = (filter: { [index: string]: string | string[] | number | Date | undefined }) => {
    let searchEndpoint = '/listRmas?'
    for (const key of Object.keys(filter)) {
      if (filter[key]) {
        searchEndpoint += `${key}=${filter[key]}&`
      }
    }
    if (user.user_type === 'Sales Rep') {
      searchEndpoint += `salesUser=${user.user_id}&`
    }

    let loading = true
    setTimeout(() => {
      setListLoading(loading)
    }, 200)

    axios
      .get(searchEndpoint)
      .then(res => {
        setRmaList(res.data.data)
        setRmaCount(res.data.count)
        setSearchError(false)
        setLoadError(false)
      })
      .catch(error => {
        console.error(error)
        setSearchError(true)
        setLoadError(true)
      })
      .finally(() => {
        loading = false
        setListLoading(false)
        setInitialLoad(false)
      })
  }

  const handleClear = () => {
    const newFilter = {
      ...rmaFilter,
      soNumber: '',
      customerPO: '',
      rmaStatus: '',
      salesRep: '',
      vendorPO: '',
      rmaInitFrom: undefined,
      rmaInitTo: undefined,
      caseNumber: '',
      opsOwner: '',
      sort: 'rma_initiation_date desc',
    }
    setRmaFilter(newFilter)
    handleSearch(newFilter)
  }

  const handleOpsOwner = (rmaId: string, owner: string | null) => {
    const refNumber = rmaList.find(rma => rma.rma_id === rmaId)?.rma_number
    axios
      .post(`/rma/${rmaId}/edit`, { id: rmaId, rma: { operations_owner: owner }, user: user.user_id })
      .then(() => {
        setRmaList(prev => prev.map(rma => (rma.rma_id === rmaId ? { ...rma, operations_owner: owner } : rma)))
        setOpsOwnerError({
          show: false,
          rmaRefNumber: '',
        })
      })
      .catch(error => {
        setOpsOwnerError({
          show: true,
          rmaRefNumber: refNumber ?? rmaId,
        })
        console.error(error)
      })
  }

  return (
    <div className="rma">
      {loadError ? (
        <div className="rma-list-load-error-container">
          <ErrorScreen
            errorText="Something went wrong while loading the RMA list. Please refresh the page or contact support if the error persists."
            title="RMA List Load Failed"
          />
        </div>
      ) : (
        <>
          <Container maxWidth={false} className="rma-search-container">
            {listLoading && initialLoad ? (
              <>
                <Skeleton width={120} height={32} />
                <Divider />
                <Grid container spacing={2} className="rma-search-grid">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <Grid size={3} className="rma-search-fields" key={index}>
                      <Skeleton width={100} />
                      <Skeleton variant="rounded" width="100%" height={40} />
                    </Grid>
                  ))}
                </Grid>
                <div className="rma-search-buttons">
                  <Button variant="outlined" loading={listLoading}>
                    Clear
                  </Button>
                  <Button variant="contained" loading={listLoading}>
                    Search
                  </Button>
                </div>
              </>
            ) : (
              <form>
                <Typography variant="h6" className="rma-search-header">
                  RMA Search
                </Typography>
                <Divider />
                <Grid container spacing={2} className="rma-search-grid">
                  <Grid size={3} className="rma-search-fields">
                    <Typography variant="body2">Sales Order Number:</Typography>
                    <TextField
                      size="small"
                      label="Sales Order Number"
                      value={rmaFilter.soNumber}
                      onChange={e => setRmaFilter({ ...rmaFilter, soNumber: e.target.value })}
                    />
                  </Grid>
                  <Grid size={3} className="rma-search-fields">
                    <Typography variant="body2">Customer PO:</Typography>
                    <TextField
                      size="small"
                      label="Customer PO"
                      value={rmaFilter.customerPO}
                      onChange={e => setRmaFilter({ ...rmaFilter, customerPO: e.target.value })}
                    />
                  </Grid>
                  <Grid size={3} className="rma-search-fields">
                    <Typography variant="body2">RMA Status:</Typography>
                    <TextField
                      size="small"
                      label="RMA Status"
                      select
                      value={rmaFilter.rmaStatus}
                      onChange={e => setRmaFilter({ ...rmaFilter, rmaStatus: parseInt(e.target.value) })}
                    >
                      {rmaStatuses.map(status => (
                        <MenuItem value={status.id} key={status.id}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={3} className="rma-search-fields">
                    <Typography variant="body2">Sales Rep:</Typography>
                    <Autocomplete
                      autoHighlight
                      options={salesReps}
                      value={salesReps.find(rep => rep.initial === rmaFilter.salesRep) ?? null}
                      getOptionLabel={option => `${option.initial} ${getRepCodeName(option)}`}
                      renderInput={params => <TextField {...params} size="small" label="Sales Rep" />}
                      fullWidth
                      onChange={(_e, value) => {
                        setRmaFilter({
                          ...rmaFilter,
                          salesRep: value?.initial ?? '',
                        })
                      }}
                    />
                  </Grid>
                  <Grid size={3} className="rma-search-fields">
                    <Typography variant="body2">RMA Case Number:</Typography>
                    <TextField
                      size="small"
                      label="Case Number"
                      value={rmaFilter.caseNumber}
                      onChange={e => setRmaFilter({ ...rmaFilter, caseNumber: e.target.value })}
                    />
                  </Grid>
                  <Grid size={3} className="rma-search-fields">
                    <Typography variant="body2">Vendor PO:</Typography>
                    <TextField
                      size="small"
                      label="Vendor PO"
                      value={rmaFilter.vendorPO}
                      onChange={e => setRmaFilter({ ...rmaFilter, vendorPO: e.target.value })}
                    />
                  </Grid>
                  <Grid size={3} className="rma-search-fields">
                    <Typography variant="body2">RMA Initiation Date:</Typography>
                    <div>
                      <DatePicker
                        label="From Date"
                        size="small"
                        value={rmaFilter.rmaInitFrom ? dayjs.utc(rmaFilter.rmaInitFrom as Date | undefined) : null}
                        onChange={e => setRmaFilter({ ...rmaFilter, rmaInitFrom: e?.format('YYYY-MM-DD') })}
                      />
                      <DatePicker
                        label="To Date"
                        size="small"
                        value={rmaFilter.rmaInitTo ? dayjs.utc(rmaFilter.rmaInitTo as Date | undefined) : null}
                        onChange={e => setRmaFilter({ ...rmaFilter, rmaInitTo: e?.format('YYYY-MM-DD') })}
                      />
                    </div>
                  </Grid>
                  <Grid size={3} className="rma-search-fields">
                    <Typography variant="body2">Ops Owner:</Typography>
                    <TextField
                      size="small"
                      label="Ops Owner"
                      select
                      value={rmaFilter.opsOwner}
                      onChange={e => setRmaFilter({ ...rmaFilter, opsOwner: e.target.value })}
                    >
                      {operations.map(opsOwner => (
                        <MenuItem value={opsOwner.user_id} key={opsOwner.user_id}>
                          {opsOwner.display_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <div className="rma-search-buttons">
                  <Button variant="outlined" onClick={handleClear} loading={listLoading && initialLoad}>
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    onClick={e => {
                      e.preventDefault()
                      handleSearch(rmaFilter)
                    }}
                    loading={listLoading && initialLoad}
                  >
                    Search
                  </Button>
                </div>
              </form>
            )}
          </Container>

          {searchError ? (
            <div className="rma-list-error-container">
              <ErrorScreen
                errorText="Something went wrong while searching. Please try again or contact support if the error persists."
                title="Search Failed"
              />
            </div>
          ) : (
            <TableContainer className="rma-list-table">
              <div className="rma-list-table-header">
                {initialLoad && listLoading ? (
                  <Skeleton width={120} height={32} />
                ) : (
                  <Typography variant="h6">RMA List</Typography>
                )}
                {initialLoad && listLoading ? (
                  <Skeleton width={300} height={32} />
                ) : (
                  <TablePagination
                    component="div"
                    count={rmaCount}
                    page={rmaFilter.page as number}
                    rowsPerPage={rmaFilter.limit as number}
                    onPageChange={(_e, newPage) => {
                      const newFilter = { ...rmaFilter, page: newPage }
                      setRmaFilter(newFilter)
                      handleSearch(newFilter)
                    }}
                    onRowsPerPageChange={e => {
                      const newFilter = { ...rmaFilter, limit: parseInt(e.target.value) }
                      setRmaFilter(newFilter)
                      handleSearch(newFilter)
                    }}
                  />
                )}
              </div>
              <Table stickyHeader color="primary">
                <TableHead>
                  {initialLoad && listLoading ? (
                    <TableRow>
                      {Array.from({ length: 8 }).map((_, index) => (
                        <TableCell key={index}>
                          <Skeleton className="skeleton-header" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={(rmaFilter.sort as string).includes('rma_number')}
                          direction={rmaFilter.sort === 'rma_number' ? 'asc' : 'desc'}
                          onClick={() => {
                            const newFilter = {
                              ...rmaFilter,
                              sort: rmaFilter.sort === 'rma_number' ? 'rma_number desc' : 'rma_number',
                            }
                            setRmaFilter(newFilter)
                            handleSearch(newFilter)
                          }}
                        >
                          Case Number
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel>Operations Owner</TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={(rmaFilter.sort as string).includes('so_number')}
                          direction={rmaFilter.sort === 'so_number' ? 'asc' : 'desc'}
                          onClick={() => {
                            const newFilter = {
                              ...rmaFilter,
                              sort: rmaFilter.sort === 'so_number' ? 'so_number desc' : 'so_number',
                            }
                            setRmaFilter(newFilter)
                            handleSearch(newFilter)
                          }}
                        >
                          SO Number
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={(rmaFilter.sort as string).includes('customer_po')}
                          direction={rmaFilter.sort === 'customer_po' ? 'asc' : 'desc'}
                          onClick={() => {
                            const newFilter = {
                              ...rmaFilter,
                              sort: rmaFilter.sort === 'customer_po' ? 'customer_po desc' : 'customer_po',
                            }
                            setRmaFilter(newFilter)
                            handleSearch(newFilter)
                          }}
                        >
                          Customer PO
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={(rmaFilter.sort as string).includes('rep_code')}
                          direction={rmaFilter.sort === 'rep_code' ? 'asc' : 'desc'}
                          onClick={() => {
                            const newFilter = {
                              ...rmaFilter,
                              sort: rmaFilter.sort === 'rep_code' ? 'rep_code desc' : 'rep_code',
                            }
                            setRmaFilter(newFilter)
                            handleSearch(newFilter)
                          }}
                        >
                          Sales Rep
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={(rmaFilter.sort as string).includes('rma_initiation_date')}
                          direction={rmaFilter.sort === 'rma_initiation_date' ? 'asc' : 'desc'}
                          onClick={() => {
                            const newFilter = {
                              ...rmaFilter,
                              sort:
                                rmaFilter.sort === 'rma_initiation_date'
                                  ? 'rma_initiation_date desc'
                                  : 'rma_initiation_date',
                            }
                            setRmaFilter(newFilter)
                            handleSearch(newFilter)
                          }}
                        >
                          RMA Initiation Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={(rmaFilter.sort as string).includes('rma_status')}
                          direction={rmaFilter.sort === 'rma_status' ? 'asc' : 'desc'}
                          onClick={() => {
                            const newFilter = {
                              ...rmaFilter,
                              sort: rmaFilter.sort === 'rma_status' ? 'rma_status desc' : 'rma_status',
                            }
                            setRmaFilter(newFilter)
                            handleSearch(newFilter)
                          }}
                        >
                          RMA Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={(rmaFilter.sort as string).includes('last_update_date')}
                          direction={rmaFilter.sort === 'last_update_date' ? 'asc' : 'desc'}
                          onClick={() => {
                            const newFilter = {
                              ...rmaFilter,
                              sort:
                                rmaFilter.sort === 'last_update_date' ? 'last_update_date desc' : 'last_update_date',
                            }
                            setRmaFilter(newFilter)
                            handleSearch(newFilter)
                          }}
                        >
                          Last Modified
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {listLoading && !initialLoad
                    ? Array.from({ length: rmaFilter.limit as number }).map((_, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {Array.from({ length: 8 }).map((_, colIndex) => (
                            <TableCell key={colIndex}>
                              <Skeleton />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : rmaList.map(rma => (
                        <TableRow key={rma.rma_id}>
                          <TableCell>
                            <LinkMui to={`/rma/${rma.rma_id}`} component={LinkRouter}>
                              {rma.rma_number}
                            </LinkMui>
                          </TableCell>
                          <TableCell>
                            <OpsOwner
                              key={rma.rma_id}
                              operationsOwner={rma.operations_owner}
                              objectId={rma.rma_id}
                              handleEditOpsOwner={handleOpsOwner}
                            />
                          </TableCell>
                          <TableCell align="right">{rma.so_number}</TableCell>
                          <TableCell>{rma.customer_po}</TableCell>
                          <TableCell>{`${rma.rep_code} ${getRepCodeName(
                            salesReps.find(rep => rep.initial === rma.rep_code),
                          )}`}</TableCell>
                          <TableCell align="right">{dayjs.utc(rma.rma_initiation_date).format('MM/DD/YYYY')}</TableCell>
                          <TableCell>{rma.rma_status}</TableCell>
                          <TableCell align="right">
                            {rma.last_update_date ? dayjs.utc(rma.last_update_date).format('MM/DD/YYYY') : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
              <TablePagination
                className="rma-list-table-pagination-footer"
                component="div"
                count={rmaCount}
                page={rmaFilter.page as number}
                rowsPerPage={rmaFilter.limit as number}
                onPageChange={(_e, newPage) => {
                  setRmaFilter({ ...rmaFilter, page: newPage })
                }}
                onRowsPerPageChange={e => {
                  setRmaFilter({ ...rmaFilter, limit: parseInt(e.target.value) })
                }}
              />
            </TableContainer>
          )}
          <Snackbar open={opsOwnerError.show} onClose={() => setOpsOwnerError({ show: false, rmaRefNumber: '' })}>
            <Alert severity="error" className="sales-order-error-alert">
              <AlertTitle>Operations Owner Not Saved</AlertTitle>
              An error occurred while saving the operations owner to SO #{opsOwnerError.rmaRefNumber}. Please try again
              or contact support if the error persists.
            </Alert>
          </Snackbar>
        </>
      )}
    </div>
  )
}

export default RmaList
